import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AUTO_ROTATE_MS,
  PAUSE_AFTER_INTERACTION_MS,
  SLIDE_DURATION_MS
} from './constants';

interface UseBannerCarouselOptions {
  count: number;
  onIndexChange: (index: number) => void;
}

/**
 * Drives an infinite-loop sliding carousel.
 *
 * The rendered track has `count + 2` slots:
 *   [clone-of-last] [0] [1] … [n-1] [clone-of-first]
 *
 * `slidePos` is the index into that extended track (1-based real items).
 * After sliding to a clone slot the position is silently snapped back
 * to the real counterpart without a CSS transition.
 */
const useBannerCarousel = ({
  count,
  onIndexChange
}: UseBannerCarouselOptions) => {
  const [slidePos, setSlidePos] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  const countRef = useRef(count);
  countRef.current = count;
  const slidePosRef = useRef(slidePos);
  slidePosRef.current = slidePos;
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPausedRef = useRef(false);
  const snapRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback(
    (fromPos: number) => {
      clearTimer();
      if (countRef.current <= 1 || isPausedRef.current) return;
      timerRef.current = setTimeout(() => {
        setIsAnimating(true);
        setSlidePos(fromPos + 1);
      }, AUTO_ROTATE_MS);
    },
    [clearTimer]
  );

  const scheduleNextRef = useRef(scheduleNext);
  scheduleNextRef.current = scheduleNext;

  const realIndex = useCallback(
    (pos: number) => (((pos - 1) % count) + count) % count,
    [count]
  );

  useEffect(() => {
    if (snapRef.current) clearTimeout(snapRef.current);

    const idx = realIndex(slidePos);
    onIndexChangeRef.current(idx);

    const isAtCloneEnd = slidePos >= count + 1;
    const isAtCloneStart = slidePos <= 0;

    if (isAtCloneEnd || isAtCloneStart) {
      const snapTarget = isAtCloneEnd ? 1 : count;
      snapRef.current = setTimeout(() => {
        setIsAnimating(false);
        setSlidePos(snapTarget);
      }, SLIDE_DURATION_MS);
      return;
    }

    if (!isPausedRef.current) {
      scheduleNextRef.current(slidePos);
    }
  }, [slidePos, count, realIndex]);

  useEffect(() => {
    if (!isAnimating) {
      const id = requestAnimationFrame(() => {
        setIsAnimating(true);
        if (!isPausedRef.current) {
          scheduleNextRef.current(slidePosRef.current);
        }
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [isAnimating]);

  useEffect(
    () => () => {
      clearTimer();
      if (pauseRef.current) clearTimeout(pauseRef.current);
      if (snapRef.current) clearTimeout(snapRef.current);
    },
    [clearTimer]
  );

  const pauseRotation = useCallback(() => {
    isPausedRef.current = true;
    clearTimer();
    if (pauseRef.current) clearTimeout(pauseRef.current);
    pauseRef.current = setTimeout(() => {
      isPausedRef.current = false;
      scheduleNextRef.current(slidePosRef.current);
    }, PAUSE_AFTER_INTERACTION_MS);
  }, [clearTimer]);

  const goTo = useCallback(
    (index: number) => {
      setIsAnimating(true);
      setSlidePos(index + 1);
      pauseRotation();
    },
    [pauseRotation]
  );

  return { slidePos, isAnimating, goTo };
};

export default useBannerCarousel;
